import React, { useState, useEffect, useRef, useMemo } from "react";
import { Volume2, VolumeX, Mic } from "lucide-react";
import { enqueueRhodeQueue } from "../utils/rhodeQueue";
import { selectRhodeVoice } from "../utils/rhodeVoice";
import {
  buildRhodeItemsSummary,
  buildUpcomingItemsAnnouncement,
  buildSuiteReminderAnnouncement,
  buildSuiteTakeoverAnnouncement,
  hasExplicitCourse,
} from "../utils/rhodeCourseAnnouncements";
import {
  buildAggregatorDelayAnnouncement,
  buildAggregatorNewOrderAnnouncement,
  resolveOrderAggregator,
} from "../utils/aggregatorPresentation";
import { buildRhodeDispatchAnnouncement } from "../utils/rhodeDispatchAnnouncements";

const DEFAULT_REMOTE_CONFIG = {
  announce_server_name: true,
  announce_repeat_table: true,
  announce_items_with_qty: true,
  announce_delay_reminder: true,
  delay_initial_minutes: 10,
  delay_repeat_minutes: 5,
  volume: 0.8,
  rate: 1,
  pitch: 1,
};

const toFirstName = (value) => {
  const raw = (value || "").toString().trim();
  if (!raw) return "";
  const [firstToken] = raw.split(/\s+/);
  return (firstToken || "").trim();
};

const RhodeAssistant = React.forwardRef(
  (
    {
      isEnabled = false,
      onToggle,
      onButtonClick,
      interface_type = "cuisine",
      interface_label = null,
      className = "",
      position = "top-right",
      remoteConfig = null,
    },
    ref,
  ) => {
    const [isActive, setIsActive] = useState(Boolean(isEnabled));
    const [isSupported, setIsSupported] = useState(false);
    const [selectedVoice, setSelectedVoice] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    const messageQueueRef = useRef([]);
    const isConsumingRef = useRef(false);
    const speechBlockedRef = useRef(false);
    const currentAnnouncementRef = useRef(null);
    const lastMessageRef = useRef({ text: "", at: 0 });
    const isActiveRef = useRef(Boolean(isEnabled));
    const isSupportedRef = useRef(false);
    const effectiveConfigRef = useRef(DEFAULT_REMOTE_CONFIG);
    const selectedVoiceRef = useRef(null);
    const loadVoicesRef = useRef(() => {});
    const cancellationBatchRef = useRef([]);
    const cancellationTimerRef = useRef(null);
    const activeAnnouncementPayloadsRef = useRef(new Map());
    const announcementsSyncedRef = useRef(false);

    useEffect(
      () => () => {
        isActiveRef.current = false;
        messageQueueRef.current = [];
        currentAnnouncementRef.current = null;
        isConsumingRef.current = false;
        if (cancellationTimerRef.current) {
          clearTimeout(cancellationTimerRef.current);
          cancellationTimerRef.current = null;
        }
        if ("speechSynthesis" in window) {
          window.speechSynthesis.cancel();
        }
      },
      [],
    );

    const effectiveConfig = useMemo(() => {
      const merged = { ...DEFAULT_REMOTE_CONFIG, ...(remoteConfig || {}) };
      return {
        ...merged,
        volume: Math.max(0, Math.min(1, Number(merged.volume ?? 0.8))),
        rate: Math.max(0.5, Math.min(2, Number(merged.rate ?? 1))),
        pitch: Math.max(0.5, Math.min(2, Number(merged.pitch ?? 1))),
      };
    }, [remoteConfig]);

    useEffect(() => {
      effectiveConfigRef.current = effectiveConfig;
    }, [effectiveConfig]);

    useEffect(() => {
      selectedVoiceRef.current = selectedVoice;
      if (selectedVoice && messageQueueRef.current.length) {
        const timer = setTimeout(() => consumeQueue(), 0);
        return () => clearTimeout(timer);
      }
      return undefined;
      // consumeQueue lit exclusivement les refs courantes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVoice]);

    useEffect(() => {
      const next = Boolean(isEnabled);
      isActiveRef.current = next;
      setIsActive(next);
      if (!next) {
        messageQueueRef.current = [];
        currentAnnouncementRef.current?.cancel?.();
        currentAnnouncementRef.current = null;
        isConsumingRef.current = false;
        setIsSpeaking(false);
        if ("speechSynthesis" in window) {
          window.speechSynthesis.cancel();
        }
      }
    }, [isEnabled]);

    useEffect(() => {
      if (!("speechSynthesis" in window)) {
        isSupportedRef.current = false;
        setIsSupported(false);
        return;
      }
      isSupportedRef.current = true;
      setIsSupported(true);

      const synth = window.speechSynthesis;
      let voicePoll = null;
      let stopVoicePoll = null;
      const loadVoices = () => {
        const available = synth.getVoices() || [];
        if (available.length === 0) return;
        const savedName = localStorage.getItem(`rhode_${interface_type}_voice`);
        const chosen = selectRhodeVoice(available, savedName);
        selectedVoiceRef.current = chosen || null;
        setSelectedVoice(chosen || null);
        if (chosen) {
          localStorage.setItem(`rhode_${interface_type}_voice`, chosen.name);
          if (voicePoll) {
            clearInterval(voicePoll);
            voicePoll = null;
          }
        }
      };

      loadVoicesRef.current = loadVoices;
      loadVoices();
      if (!selectedVoiceRef.current) {
        voicePoll = setInterval(loadVoices, 250);
        stopVoicePoll = setTimeout(() => {
          if (voicePoll) {
            clearInterval(voicePoll);
            voicePoll = null;
          }
        }, 10000);
      }
      if (typeof synth.addEventListener === "function") {
        synth.addEventListener("voiceschanged", loadVoices);
      } else {
        synth.onvoiceschanged = loadVoices;
      }
      return () => {
        loadVoicesRef.current = () => {};
        if (voicePoll) clearInterval(voicePoll);
        if (stopVoicePoll) clearTimeout(stopVoicePoll);
        if (typeof synth.removeEventListener === "function") {
          synth.removeEventListener("voiceschanged", loadVoices);
        } else if (synth.onvoiceschanged === loadVoices) {
          synth.onvoiceschanged = null;
        }
      };
    }, [interface_type]);

    useEffect(() => {
      if (!isSupported || !isActive) return undefined;
      const resumeRhode = () => {
        loadVoicesRef.current();
        speechBlockedRef.current = false;
        if (window.speechSynthesis?.paused) {
          window.speechSynthesis.resume();
        }
        if (selectedVoiceRef.current && messageQueueRef.current.length) {
          consumeQueue();
        }
      };
      window.addEventListener("pointerdown", resumeRhode, true);
      window.addEventListener("keydown", resumeRhode, true);
      return () => {
        window.removeEventListener("pointerdown", resumeRhode, true);
        window.removeEventListener("keydown", resumeRhode, true);
      };
      // consumeQueue lit exclusivement les refs courantes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, isSupported]);

    const enqueueMessage = (text, urgent = false, metadata = null) => {
      if (!isSupportedRef.current || !isActiveRef.current || !text) return;
      const now = Date.now();
      if (
        lastMessageRef.current.text === text &&
        now - lastMessageRef.current.at < 4000
      ) {
        return;
      }
      lastMessageRef.current = { text, at: now };
      messageQueueRef.current = enqueueRhodeQueue(
        messageQueueRef.current,
        metadata ? { ...metadata, text } : text,
        urgent,
      );
      // Ne jamais parler avec la voix système par défaut pendant que Chrome
      // charge encore les voix: la file repart dès que la voix Rhode est prête.
      if (selectedVoiceRef.current) consumeQueue();
    };

    const consumeQueue = () => {
      if (isConsumingRef.current) return;
      if (speechBlockedRef.current) return;
      if (!messageQueueRef.current.length) return;
      const queuedEntry = messageQueueRef.current.shift();
      const entry =
        queuedEntry && typeof queuedEntry === "object"
          ? queuedEntry
          : { text: queuedEntry };
      if (
        ["new-order", "delay-alert"].includes(entry?.kind) &&
        announcementsSyncedRef.current &&
        !activeAnnouncementPayloadsRef.current.has(String(entry?.key || ""))
      ) {
        setTimeout(consumeQueue, 0);
        return;
      }
      if (["new-order", "delay-alert"].includes(entry?.kind)) {
        const livePayload = activeAnnouncementPayloadsRef.current.get(
          String(entry?.key || ""),
        );
        if (
          livePayload &&
          (entry.kind === "new-order" ||
            String(livePayload?.dispatchKind || "").toLowerCase() === "claim")
        ) {
          entry.text = buildNewOrderMessage(livePayload);
        }
      }
      const text = String(entry?.text || "").trim();
      if (!text) return;
      isConsumingRef.current = true;
      setIsSpeaking(true);

      const utterance = new SpeechSynthesisUtterance(text);
      let invalidated = false;
      const voice = selectedVoiceRef.current;
      if (!voice) {
        messageQueueRef.current.unshift(entry);
        isConsumingRef.current = false;
        setIsSpeaking(false);
        return;
      }
      const activeConfig = effectiveConfigRef.current;
      utterance.lang = "fr-FR";
      utterance.volume = activeConfig.volume;
      utterance.rate = activeConfig.rate;
      utterance.pitch = activeConfig.pitch;
      utterance.voice = voice;

      const currentAnnouncement = {
        ...entry,
        text,
        cancel: () => {
          invalidated = true;
          window.speechSynthesis.cancel();
        },
      };
      currentAnnouncementRef.current = currentAnnouncement;

      utterance.onend = () => {
        if (currentAnnouncementRef.current === currentAnnouncement) {
          currentAnnouncementRef.current = null;
        }
        isConsumingRef.current = false;
        setIsSpeaking(false);
        setTimeout(consumeQueue, 40);
      };
      utterance.onerror = (event) => {
        if (currentAnnouncementRef.current === currentAnnouncement) {
          currentAnnouncementRef.current = null;
        }
        isConsumingRef.current = false;
        setIsSpeaking(false);
        if (!invalidated && event?.error === "not-allowed") {
          speechBlockedRef.current = true;
          messageQueueRef.current.unshift(entry);
          return;
        }
        setTimeout(consumeQueue, 80);
      };

      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }
      window.speechSynthesis.speak(utterance);
    };

    const getLocationPhrases = (payload) => {
      const data =
        typeof payload === "object" && payload
          ? payload
          : { tableNumber: payload };
      const isCounter =
        String(data.context || "").toLowerCase() === "counter" ||
        String(data.orderType || data.order_type || "").toLowerCase() ===
          "takeaway";
      if (isCounter) {
        return {
          inline: "à emporter",
          preposition: "pour une commande à emporter",
          possessive: "de la commande à emporter",
        };
      }
      if (data.isDelivery) {
        const aggregator = resolveOrderAggregator(data);
        if (aggregator) {
          return {
            inline: aggregator.label,
            preposition: `sur une commande ${aggregator.label}`,
            possessive: aggregator.label,
          };
        }
        const label = data.customerName || data.tableNumber || "Livraison";
        const suffix = label && label !== "Livraison" ? ` ${label}` : "";
        return {
          inline: `en livraison${suffix}`,
          preposition: `sur une livraison${suffix}`,
          possessive: `en livraison${suffix}`,
        };
      }
      const tableLabel = data.tableNumber || data.table || "inconnue";
      return {
        inline: `table ${tableLabel}`,
        preposition: `sur la table ${tableLabel}`,
        possessive: `de la table ${tableLabel}`,
      };
    };

    const buildNewOrderMessage = (payload) => {
      const data =
        typeof payload === "object" && payload
          ? payload
          : { tableNumber: payload };
      const courseNumber = Number(data.courseNumber || data.course_number);
      const dispatchAnnouncement = buildRhodeDispatchAnnouncement(data);
      if (dispatchAnnouncement) return dispatchAnnouncement;
      const suiteAnnouncement = buildSuiteTakeoverAnnouncement(data);
      if (suiteAnnouncement) {
        const itemsSummary = effectiveConfig.announce_items_with_qty
          ? buildRhodeItemsSummary(data.items)
          : "";
        return `${suiteAnnouncement}${itemsSummary ? ` Articles: ${itemsSummary}.` : ""}`;
      }
      const aggregatorHead = buildAggregatorNewOrderAnnouncement(data, {
        announceServerName: effectiveConfig.announce_server_name,
      });
      if (aggregatorHead) {
        const itemsSummary = effectiveConfig.announce_items_with_qty
          ? buildRhodeItemsSummary(data.items)
          : "";
        return `${aggregatorHead}.${itemsSummary ? ` Articles: ${itemsSummary}.` : ""}`;
      }
      const location = getLocationPhrases(data);
      const serverName = toFirstName(data.serverName || "");
      const hasCourse = hasExplicitCourse(data);
      const courseLabel =
        hasCourse && Number.isInteger(courseNumber) && courseNumber > 0
          ? ` le tour ${courseNumber}`
          : " une nouvelle commande";
      const isCounter =
        String(data.context || "").toLowerCase() === "counter" ||
        String(data.orderType || data.order_type || "").toLowerCase() ===
          "takeaway";
      const head =
        effectiveConfig.announce_server_name && serverName
        ? isCounter
          ? `${serverName} a envoyé une commande à emporter`
            : `${serverName} a envoyé${courseLabel}`
        : isCounter
          ? "Nouvelle commande à emporter reçue"
            : hasCourse && Number.isInteger(courseNumber) && courseNumber > 0
              ? `Tour ${courseNumber} reçu`
          : "Nouvelle commande reçue";
      const repeat =
        effectiveConfig.announce_repeat_table &&
        data.isRepeatTable &&
        !data.isDelivery
        ? " sur une table déjà en service"
        : "";
      const itemsSummary = effectiveConfig.announce_items_with_qty
        ? buildRhodeItemsSummary(data.items)
        : "";
      const locationSuffix = isCounter ? "" : ` ${location.inline}`;
      const upcomingAnnouncement = buildUpcomingItemsAnnouncement(data);
      return `${head}${locationSuffix}${repeat}.${itemsSummary ? ` Articles: ${itemsSummary}.` : ""}${upcomingAnnouncement ? ` ${upcomingAnnouncement}` : ""}`;
    };

    const flushCancellationBatch = () => {
      cancellationTimerRef.current = null;
      const batch = cancellationBatchRef.current.splice(0);
      if (!batch.length) return;
      const first = batch[0] || {};
      const quantities = new Map();
      for (const entry of batch) {
        const name =
          String(entry?.productName || "article").trim() || "article";
        quantities.set(
          name,
          Number(quantities.get(name) || 0) +
            Number(entry?.cancelledQuantity || 1),
        );
      }
      const labels = Array.from(quantities.entries()).map(
        ([name, quantity]) => `${quantity} ${name}`,
      );
      const itemSummary =
        labels.length > 1
        ? `${labels.slice(0, -1).join(", ")} et ${labels[labels.length - 1]}`
        : labels[0] || "un article";
      const actor = toFirstName(first.cancelledByName || first.userName || "");
      const location = getLocationPhrases(first);
      enqueueMessage(
        actor
          ? `${actor} a annulé ${itemSummary} ${location.preposition}.`
          : `Annulation de ${itemSummary} ${location.preposition}.`,
        true,
      );
    };

    const scheduleCancellationAnnouncement = (payload) => {
      cancellationBatchRef.current.push(payload || {});
      if (cancellationTimerRef.current) return;
      cancellationTimerRef.current = setTimeout(flushCancellationBatch, 350);
    };

    React.useImperativeHandle(ref, () => ({
      speakNewOrder: (payload) => {
        const key = String(payload?.announcementKey || "");
        if (key) activeAnnouncementPayloadsRef.current.set(key, payload);
        enqueueMessage(buildNewOrderMessage(payload), false, {
          kind: "new-order",
          key,
        });
      },
      syncPendingOrders: (payloads = []) => {
        const activePayloads = new Map(
          (Array.isArray(payloads) ? payloads : [])
            .filter((payload) => payload?.announcementKey)
            .map((payload) => [String(payload.announcementKey), payload]),
        );
        activeAnnouncementPayloadsRef.current = activePayloads;
        announcementsSyncedRef.current = true;
        messageQueueRef.current = messageQueueRef.current
          .map((entry) => {
            if (
              !entry ||
              typeof entry !== "object" ||
              !["new-order", "delay-alert"].includes(entry.kind)
            ) {
              return entry;
            }
            const payload = activePayloads.get(String(entry.key));
            if (!payload) return null;
            return entry.kind === "new-order" ||
              String(payload?.dispatchKind || "").toLowerCase() === "claim"
              ? { ...entry, text: buildNewOrderMessage(payload) }
              : entry;
          })
          .filter(Boolean);

        const current = currentAnnouncementRef.current;
        if (!["new-order", "delay-alert"].includes(current?.kind)) return;
        const payload = activePayloads.get(String(current.key));
        const nextText = payload
          ? current.kind === "new-order" ||
            String(payload?.dispatchKind || "").toLowerCase() === "claim"
            ? buildNewOrderMessage(payload)
            : current.text
          : "";
        if (nextText === current.text) return;
        current.cancel?.();
        currentAnnouncementRef.current = null;
        isConsumingRef.current = false;
        setIsSpeaking(false);
        if (nextText) {
          messageQueueRef.current = enqueueRhodeQueue(
            messageQueueRef.current,
            {
              text: nextText,
              kind: current.kind,
              key: String(payload.announcementKey),
            },
            true,
          );
        }
        setTimeout(consumeQueue, 0);
      },
      speakCancellation: scheduleCancellationAnnouncement,
      speakServerTransfer: (payload) => {
        const serverName = toFirstName(payload?.newOwnerName || "");
        const tableNumber = payload?.tableNumber || payload?.table;
        if (!serverName || !tableNumber) return;
        enqueueMessage(
          `${serverName} est désormais en charge de la table ${tableNumber}.`,
          true,
          {
            kind: "server-transfer",
            key: `table:${tableNumber}:owner:${payload?.newOwnerUserId || serverName}`,
          },
        );
      },
      speakDelayAlert: (payload) => {
        if (!effectiveConfig.announce_delay_reminder) return;
        const metadata = {
          kind: "delay-alert",
          key: String(payload?.announcementKey || ""),
        };
        if (String(payload?.dispatchKind || "").toLowerCase() === "claim") {
          enqueueMessage(buildNewOrderMessage(payload), true, metadata);
          return;
        }
        const aggregatorMessage = buildAggregatorDelayAnnouncement(payload);
        if (aggregatorMessage) {
          enqueueMessage(aggregatorMessage, true, metadata);
          return;
        }
        const suiteReminder = buildSuiteReminderAnnouncement(payload);
        if (suiteReminder) {
          enqueueMessage(suiteReminder, true, metadata);
          return;
        }
        const location = getLocationPhrases(payload);
        enqueueMessage(
          `Rappel retard. La commande ${location.possessive} n'est pas encore acceptée.`,
          true,
          metadata,
        );
      },
      speakCustomMessage: (message, priority = "normal") =>
        enqueueMessage(message, priority === "urgent"),
      isEnabled: () =>
        Boolean(
          isActiveRef.current &&
            isSupportedRef.current,
        ),
      stop: () => {
        messageQueueRef.current = [];
        currentAnnouncementRef.current?.cancel?.();
        currentAnnouncementRef.current = null;
        cancellationBatchRef.current = [];
        if (cancellationTimerRef.current) {
          clearTimeout(cancellationTimerRef.current);
          cancellationTimerRef.current = null;
        }
        isConsumingRef.current = false;
        speechBlockedRef.current = false;
        setIsSpeaking(false);
        if ("speechSynthesis" in window) {
          window.speechSynthesis.cancel();
        }
      },
    }));

    const toggleActive = () => {
      if (typeof onButtonClick === "function") {
        onButtonClick();
      }
      const next = !isActive;
      isActiveRef.current = next;
      setIsActive(next);
      if (onToggle) onToggle(next);
      localStorage.setItem(
        `rhode_${interface_type}_enabled_local`,
        next ? "1" : "0",
      );
      if (next) {
        loadVoicesRef.current();
        speechBlockedRef.current = false;
        if (window.speechSynthesis?.paused) {
          window.speechSynthesis.resume();
        }
        const spokenInterface =
          interface_label ||
          (interface_type === "bar"
            ? "le bar"
            : interface_type === "cuisine"
              ? "la cuisine"
              : interface_type);
        enqueueMessage(`Rhode activée pour ${spokenInterface}.`);
      } else {
        messageQueueRef.current = [];
        currentAnnouncementRef.current?.cancel?.();
        currentAnnouncementRef.current = null;
        isConsumingRef.current = false;
        setIsSpeaking(false);
        if ("speechSynthesis" in window) {
          window.speechSynthesis.cancel();
        }
      }
    };

    if (!isSupported) return null;

    const positionClasses =
      position === "header" ? "flex items-center" : "fixed top-6 right-6 z-50";

    return (
      <div className={positionClasses}>
        <button
          onClick={toggleActive}
          className={`
            flex items-center gap-2 px-3 py-2 rounded-2xl border backdrop-blur transition-all
            ${
              isActive
                ? "bg-emerald-500/20 border-emerald-400/50 text-emerald-100"
                : "bg-white/10 border-white/20 text-white/80"
            }
            ${className}
          `}
          title={isActive ? "Désactiver Rhode" : "Activer Rhode"}
        >
          {isActive ? (
            <Volume2 className="w-4 h-4" />
          ) : (
            <VolumeX className="w-4 h-4" />
          )}
          <span className="text-sm font-semibold">Rhode</span>
          {isSpeaking && <Mic className="w-4 h-4 animate-pulse" />}
        </button>
      </div>
    );
  },
);

export default RhodeAssistant;
